import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  TextField,
  Avatar,
  Tabs,
  Tab,
  Pagination,
  InputAdornment,
  Stack,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Collapse,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Skeleton,
  TextareaAutosize,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Delete,
  Edit,
  Refresh,
  Visibility,
  Person,
  AttachMoney,
  Comment,
  DeleteSweep,
  ArrowBack,
  Search,
  QrCode2,
  Download,
  Print,
  Close,
  Image as ImageIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Receipt,
  FileCopy,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Cancel, // Add this icon
  AccountBalanceWallet, // Add this icon
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useDeleteCommentMutation,
  useGenerateQRMutation,
  useGenerateInvoiceMutation,
  useGenerateCombinedMutation,
  useLazyGetQrImageQuery,
  useCancelOrderMutation, // Add this import
  ORDER_STATUS_OPTIONS,
  Order,
  OrderComment,
  determineOrderType,
  GenerateCombinedResponse,
} from '../../features/admin/OrderlistApi.ts';

// Warehouses dropdown
import { useListWarehousesQuery } from '../../features/admin/warehouseApi.ts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-details-tabpanel-${index}`}
      aria-labelledby={`order-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Payment method options
const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash_on_delivery', label: 'Cash on Delivery' },
  { value: 'pay_later', label: 'Pay Later' },
  { value: 'online_payment', label: 'Online Payment' },
];

// Cancel reasons options
const CANCEL_REASON_OPTIONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'out_of_stock', label: 'Out of Stock' },
  { value: 'price_discrepancy', label: 'Price Discrepancy' },
  { value: 'delivery_delay', label: 'Delivery Delay' },
  { value: 'customer_unavailable', label: 'Customer Unavailable' },
  { value: 'payment_issues', label: 'Payment Issues' },
  { value: 'duplicate_order', label: 'Duplicate Order' },
  { value: 'other', label: 'Other' },
];

// Helper function to get status text by value
const getStatusText = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.label : `Status ${status}`;
};

// Helper function to get status color by value
const getStatusColor = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : '#9e9e9e';
};

// Steps for combined generation
const generationSteps = [
  {
    label: 'Generate Invoice',
    description: 'Creating invoice PDF for the order',
    icon: <Receipt />,
  },
  {
    label: 'Complete',
    description: 'Both invoice and QR code are ready',
    icon: <CheckCircle />,
  },
];

const OrderManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const qrPrintRef = useRef<HTMLDivElement>(null);
  const combinedPrintRef = useRef<HTMLDivElement>(null);
  const BASE_URL = 'https://fftestservice.sarsetech.com';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [mainTabValue, setMainTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [viewCommentsOpen, setViewCommentsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletableComments, setDeletableComments] = useState<OrderComment[]>([]);
  const [deleteAllCommentsDialogOpen, setDeleteAllCommentsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [commentText, setCommentText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [generatedQR, setGeneratedQR] = useState<{ pickupCode: string; qrCode: string } | null>(
    null
  );
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);

  // Cancel order dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedCancelReason, setSelectedCancelReason] = useState('customer_request');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelType, setCancelType] = useState<'admin' | 'user'>('admin');

  // Loading states for view details
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [deliveryAgentData, setDeliveryAgentData] = useState<any>(null);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [deliveryInfoData, setDeliveryInfoData] = useState<any>(null);

  // Combined generation states
  const [combinedDialogOpen, setCombinedDialogOpen] = useState(false);
  const [isGeneratingCombined, setIsGeneratingCombined] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [combinedResult, setCombinedResult] = useState<GenerateCombinedResponse | null>(null);
  const [combinedQrImage, setCombinedQrImage] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [paymentFilter, setPaymentFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Track expanded rows for mobile view
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // Track the last update time for auto-refresh (hidden from UI)
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // ---- Orders query with silent polling for auto-refresh ----
  const {
    data,
    isLoading,
    error,
    isError,
    refetch: refetchOrders,
  } = useListOrdersQuery(
    {
      ...(warehouseFilter !== '' && warehouseFilter !== undefined ? { warehouse_id: warehouseFilter } : {}),
      _t: lastUpdateTime,
    },
    {
      pollingInterval: 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  // Warehouses for dropdown
  const { data: warehousesData } = useListWarehousesQuery();
  const warehouses = warehousesData?.warehouses || [];

  // Update order status mutation with automatic cache update
  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const [deleteOrder, { isLoading: isDeleting }] = useDeleteOrderMutation();
  const [deleteComment, { isLoading: isDeletingComment }] = useDeleteCommentMutation();
  const [generateQR, { isLoading: isGeneratingQR }] = useGenerateQRMutation();
  const [generateInvoice] = useGenerateInvoiceMutation();
  const [generateCombined] = useGenerateCombinedMutation();
  const [triggerGetQrImage, { isLoading: isLoadingQrImage }] = useLazyGetQrImageQuery();
  
  // Add cancel order mutation
  const [cancelOrder, { isLoading: isCancelling }] = useCancelOrderMutation();

  const orders = data?.orders || [];
  const totalRecords = orders.length;

  // Silent auto-refresh effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Listen for visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Helper function to get payment method from order
  const getOrderPaymentMethod = (order: Order): string => {
    if (order.cash_on_delivery === 1) {
      return 'cash_on_delivery';
    } else if (order.pay_later === 1) {
      return 'pay_later';
    } else {
      return 'online_payment';
    }
  };

  // Helper function to get primary warehouse name from order
  const getOrderWarehouseName = (order: Order): string => {
    // Try to get warehouse from order data
    if ((order as any).warehouse_name) {
      return (order as any).warehouse_name;
    }

    // Try to get warehouse from first item
    if (order.items && order.items.length > 0) {
      const firstItem = order.items[0];
      if (firstItem.warehouse_name) {
        return firstItem.warehouse_name;
      }
      if (firstItem.warehouse && firstItem.warehouse.name) {
        return firstItem.warehouse.name;
      }
    }

    // Try to get from delivery agent warehouse
    if ((order as any).delivery_agent && (order as any).delivery_agent.warehouse_name) {
      return (order as any).delivery_agent.warehouse_name;
    }

    return '-';
  };

  // Helper function to determine delivery type
  const getDeliveryType = (order: Order | null): string => {
    if (!order || !(order as any).delivery_proof) return 'Not Delivered Yet';

    const deliveryProof = (order as any).delivery_proof;

    if (deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0) {
      return 'Normal Delivery';
    }
    if (deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0) {
      return 'Open Box Delivery';
    }
    if (deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0) {
      return 'Partial Delivery';
    }

    return 'Not Delivered Yet';
  };

  // Helper function to get delivery type color
  const getDeliveryTypeColor = (deliveryType: string): string => {
    switch (deliveryType) {
      case 'Normal Delivery': return '#4caf50';
      case 'Open Box Delivery': return '#2196f3';
      case 'Partial Delivery': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  // Helper function to get assignment ID from delivery proof
  const getAssignmentId = (order: Order | null): string => {
    if (!order || !(order as any).delivery_proof) return '-';

    const deliveryProof = (order as any).delivery_proof;

    if (deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0) {
      return deliveryProof.normal_delivery[0].assignment_id || '-';
    }
    if (deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0) {
      return deliveryProof.openbox_delivery[0].assignment_id || '-';
    }
    if (deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0) {
      return deliveryProof.partial_delivery[0].assignment_id || '-';
    }

    return '-';
  };

  // Filter orders based on search, status, and payment method
  const filteredOrders = orders.filter((order: Order) => {
    if (!searchStr && statusFilter.length === 0 && paymentFilter.length === 0) return true;

    const search = searchStr.toLowerCase();
    const matchesSearch =
      !searchStr ||
      order.id.toString().includes(search) ||
      (order.user_name && order.user_name.toLowerCase().includes(search)) ||
      order.user_id?.toString().includes(search) ||
      getOrderWarehouseName(order).toLowerCase().includes(search);

    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(order.status);

    const orderPaymentMethod = getOrderPaymentMethod(order);
    const matchesPayment = paymentFilter.length === 0 || paymentFilter.includes(orderPaymentMethod);

    return matchesSearch && matchesStatus && matchesPayment;
  });

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setStatusFilter(typeof value === 'string' ? value.split(',').map(Number) : value);
    setPage(1);
  };

  const handleWarehouseFilterChange = (event: any) => {
    const value = event.target.value;
    setWarehouseFilter(value === '' ? '' : Number(value));
    setPage(1);
  };

  const handlePaymentFilterChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setPaymentFilter(typeof value === 'string' ? value.split(',') : value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchStr('');
    setStatusFilter([]);
    setWarehouseFilter('');
    setPaymentFilter([]);
    setPage(1);
  };

  const handleToggleRow = (orderId: number) => {
    setExpandedRows(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // FIXED: Handle view order with proper data loading
  const handleViewOrder = async (order: Order) => {
    setSelectedOrder(order);
    setDetailsTabValue(0);
    setIsLoadingDetails(true);
    
    // Reset previous data
    setCustomerData(null);
    setDeliveryAgentData(null);
    setWarehouseData(null);
    setDeliveryInfoData(null);
    
    // Simulate API calls with timeouts to prevent layout shifts
    setTimeout(() => {
      // Set customer data
      setCustomerData({
        name: order.user_name,
        userId: order.user_id,
      });
      
      // Set delivery agent data
      setDeliveryAgentData({
        name: order.delivery_agent_name || 'Not assigned',
        contact: (order as any).delivery_agent?.contact || (order as any).delivery_agent?.mobile || null,
        email: (order as any).delivery_agent?.email || null,
        id: (order as any).delivery_agent?.id || null,
      });
      
      // Set warehouse data
      setWarehouseData({
        name: getOrderWarehouseName(order),
        orderDate: order.order_date,
      });
      
      // Set delivery info data
      const deliveryProof = (order as any).delivery_proof;
      setDeliveryInfoData({
        deliveryType: getDeliveryType(order),
        assignmentId: getAssignmentId(order),
        deliveryProof: deliveryProof,
      });
      
      setIsLoadingDetails(false);
      setShowDetails(true);
    }, 100);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedOrder(null);
    setIsLoadingDetails(false);
    setCustomerData(null);
    setDeliveryAgentData(null);
    setWarehouseData(null);
    setDeliveryInfoData(null);
  };

  const handleStatusUpdateClick = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.status);
    setCommentText(''); // Clear previous comment
    setStatusDialogOpen(true);
  };

  // Handle Cancel Order Click
  const handleCancelOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setCancelType('admin'); // Default to admin cancellation
    setSelectedCancelReason('customer_request');
    setCustomCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleDeleteClick = (order: Order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAllCommentsClick = (order: Order) => {
    setSelectedOrder(order);

    const deletable = (order.comments || []).filter((c) => {
      const s = Number((c as any).status);
      return !Number.isNaN(s) && requiresComment(s);
    });

    setDeletableComments(deletable);
    setDeleteAllCommentsDialogOpen(true);
  };

  const handleConfirmDeleteAllComments = async () => {
    if (!selectedOrder) return;

    if (!deletableComments || deletableComments.length === 0) {
      setSnackbar({
        open: true,
        message: 'No comments eligible for deletion (only statuses 5-17 can be deleted).',
        severity: 'info',
      });
      setDeleteAllCommentsDialogOpen(false);
      return;
    }

    try {
      const promiseList = deletableComments.map((comment) =>
        deleteComment({
          comment_id: comment.id!,
          order_id: selectedOrder.id,
        })
          .unwrap()
          .then(
            (res) => ({ status: 'fulfilled', res, commentId: comment.id }),
            (err) => ({ status: 'rejected', err, commentId: comment.id })
          )
      );

      const results = await Promise.all(promiseList);

      const failed = results.filter((r) => (r as any).status === 'rejected') as any[];
      const succeeded = results.filter((r) => (r as any).status === 'fulfilled') as any[];

      if (failed.length === 0) {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} comment(s) successfully.`,
          severity: 'success',
        });
      } else if (succeeded.length === 0) {
        const messages = failed.map(
          (f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`
        );
        setSnackbar({
          open: true,
          message: messages.join(' | '),
          severity: 'error',
        });
      } else {
        const failMsgs = failed.map(
          (f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`
        );
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} comment(s). Failed: ${failMsgs.join(' | ')}`,
          severity: 'warning',
        });
      }

      setDeleteAllCommentsDialogOpen(false);
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete all comments:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Error deleting comments',
        severity: 'error',
      });
      setDeleteAllCommentsDialogOpen(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      const result = await deleteOrder({ order_id: selectedOrder.id }).unwrap();

      setSnackbar({
        open: true,
        message: result?.message || 'Order deleted successfully!',
        severity: 'success',
      });
      setDeleteDialogOpen(false);

      if (showDetails) {
        setShowDetails(false);
        setSelectedOrder(null);
      }

      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete order:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error deleting order',
        severity: 'error',
      });
    }
  };

  // Handle Cancel Order
  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    try {
      // Determine order type
      const orderType = determineOrderType(selectedOrder);
      
      // Build cancel reason
      let finalCancelReason = '';
      if (selectedCancelReason === 'other') {
        finalCancelReason = customCancelReason.trim();
      } else {
        const reasonOption = CANCEL_REASON_OPTIONS.find(opt => opt.value === selectedCancelReason);
        finalCancelReason = reasonOption ? reasonOption.label : selectedCancelReason;
      }

      // Add prefix based on cancel type
      if (cancelType === 'admin') {
        finalCancelReason = `Admin Cancelled: ${finalCancelReason}`;
      } else {
        finalCancelReason = `User Cancelled: ${finalCancelReason}`;
      }

      const result = await cancelOrder({
        order_id: selectedOrder.id,
        order_type: orderType as 'regular' | 'guest',
        cancel_reason: finalCancelReason,
      }).unwrap();

      setSnackbar({
        open: true,
        message: result.message || 'Order cancelled successfully!',
        severity: 'success',
      });
      
      setCancelDialogOpen(false);
      
      // Update local order status
      const updatedOrder = { ...selectedOrder, status: result.status_code || 12 };
      setSelectedOrder(updatedOrder);
      
      // Show refund information if applicable
      if (selectedOrder.cash_on_delivery === 1 || selectedOrder.pay_later === 1) {
        setTimeout(() => {
          setSnackbar({
            open: true,
            message: `Amount refunded to ${selectedOrder.cash_on_delivery === 1 ? 'wallet' : 'pay later balance'}`,
            severity: 'info',
          });
        }, 1000);
      }

      refetchOrders();
      setLastUpdateTime(Date.now());

    } catch (err: any) {
      console.error('Failed to cancel order:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error cancelling order',
        severity: 'error',
      });
    }
  };

  // requiresComment function with complete status mapping
  const requiresComment = (status: number) => {
    // Statuses that require comments: 
    // 5 (In Transit), 6 (Out for delivery), 7 (Delivered), 8 (Not delivered), 
    // 9 (Cancelled by user), 10 (Return and replacement), 11 (Return and refund),
    // 12 (Order cancelled by admin), 13 (Order Delayed), 
    // 14 (Return in progress), 15 (Return completed), 17 (Return)
    return [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17].includes(status);
  };

  const handleStatusUpdate = async (comment?: string) => {
    if (!selectedOrder) return;

    try {
      const updateData: any = {
        order_id: selectedOrder.id,
        status: selectedStatus,
      };

      if (comment && comment.trim()) {
        updateData.comment = comment;
      }

      // Add comment for required statuses even if not provided by user
      if (requiresComment(selectedStatus) && (!comment || !comment.trim())) {
        const statusOption = ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus);
        const autoComment = `Status updated to: ${statusOption?.label || getStatusText(selectedStatus)}`;
        updateData.comment = autoComment;
      }

      const updatedOrder = { ...selectedOrder, status: selectedStatus };
      setSelectedOrder(updatedOrder);

      const result = await updateOrderStatus(updateData).unwrap();

      setSnackbar({
        open: true,
        message: result?.message || 'Order status updated successfully!',
        severity: 'success',
      });
      setStatusDialogOpen(false);
      setCommentDialogOpen(false);
      setCommentText('');

      refetchOrders();
      setLastUpdateTime(Date.now());

    } catch (err: any) {
      console.error('Failed to update order status:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error updating order status',
        severity: 'error',
      });

      refetchOrders();
      setLastUpdateTime(Date.now());
    }
  };

  const handleCommentSubmit = () => {
    if (requiresComment(selectedStatus) && !commentText.trim()) {
      setSnackbar({
        open: true,
        message: 'Comment is required for this status update',
        severity: 'warning',
      });
      return;
    }
    handleStatusUpdate(commentText);
    setCommentText(''); // Clear after submission
  };

  const handleViewComments = (order: Order) => {
    setSelectedOrder(order);
    setViewCommentsOpen(true);
  };

  const handleSilentRefresh = () => {
    refetchOrders();
    setLastUpdateTime(Date.now());
  };

  // status helpers
  const resolveStatusOption = (status: number | string | undefined) => {
    if (status === undefined || status === null) return null;
    const num = Number(status);
    if (Number.isNaN(num)) return null;
    return ORDER_STATUS_OPTIONS.find((opt) => opt.value === num) || null;
  };

  const inferStatusFromCommentText = (text?: string) => {
    if (!text) return null;
    const normalized = String(text).toLowerCase();
    for (const opt of ORDER_STATUS_OPTIONS) {
      if (!opt || !opt.label) continue;
      const label = String(opt.label).toLowerCase();
      if (normalized.includes(label)) return opt;
    }
    return null;
  };

  const CommentItem = ({ comment }: { comment: OrderComment }) => {
    const statusLabelFromApi = (comment as any).status_label || (comment as any).status_text || null;
    const inferredOption = inferStatusFromCommentText(comment.comment);
    const fromStatusField = resolveStatusOption((comment as any).status);

    let chipLabel: string | undefined;
    let chipColor = '#9e9e9e';

    if (statusLabelFromApi) {
      chipLabel = String(statusLabelFromApi);
      const matched = ORDER_STATUS_OPTIONS.find(
        (o) => String(o.label).toLowerCase() === String(statusLabelFromApi).toLowerCase()
      );
      if (matched) chipColor = matched.color;
    } else if (inferredOption) {
      chipLabel = inferredOption.label;
      chipColor = inferredOption.color;
    } else if (fromStatusField) {
      chipLabel = fromStatusField.label;
      chipColor = fromStatusField.color;
    } else if ((comment as any).status !== undefined && (comment as any).status !== null) {
      chipLabel = `Status ${(comment as any).status}`;
    } else {
      chipLabel = undefined;
    }

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: comment.user_type === 'admin' ? '#f8f9ff' : '#f0f8ff',
          borderRadius: 2,
          border: `1px solid ${comment.user_type === 'admin' ? '#e3f2fd' : '#e8f5e8'}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: comment.user_type === 'admin' ? '#1976d2' : '#4caf50',
                width: 32,
                height: 32,
              }}
            >
              {comment.user_type === 'admin' ? 'A' : 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {comment.user_type === 'admin' ? 'Admin' : `User: ${comment.user_name || comment.user_id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {comment.created_at ? formatDateTime(comment.created_at) : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {chipLabel && (
              <Chip
                label={chipLabel}
                size="small"
                sx={{
                  backgroundColor: chipColor,
                  color: 'white',
                  height: 20,
                }}
              />
            )}
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            pl: 6,
            fontStyle: 'italic',
            color: 'text.primary',
          }}
        >
          "{comment.comment}"
        </Typography>
      </Box>
    );
  };

  const CommentsList = ({
    orderId,
    maxHeight = 400,
    showDeleteAllButton = false,
  }: {
    orderId: number;
    maxHeight?: number;
    showDeleteAllButton?: boolean;
  }) => {
    const orderComments = getCommentsForOrder(orderId);
    const order = orders.find((o) => o.id === orderId);

    if (orderComments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No comments available for this order.
        </Typography>
      );
    }

    return (
      <Box>
        {showDeleteAllButton && orderComments.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweep />}
              onClick={() => order && handleDeleteAllCommentsClick(order)}
              disabled={isDeletingComment}
            >
              Delete All comments ({orderComments.length})
            </Button>
          </Box>
        )}

        <Box sx={{ maxHeight, overflowY: 'auto' }}>
          {orderComments
            .slice()
            .sort(
              (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
            )
            .map((comment, index) => (
              <CommentItem key={comment.id || index} comment={comment} />
            ))}
        </Box>
      </Box>
    );
  };

  // Agent info helper - FIXED with loading state
  const renderAgentInfo = (loading: boolean, data: any) => {
    if (loading) {
      return (
        <Box>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      );
    }

    if (!data || !data.name) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: '#ff6a00', textTransform: 'uppercase' }}
        >
          {data.name}
        </Typography>
        {data.contact && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {data.contact}
          </Typography>
        )}
        {data.email && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              fontSize: '11px'
            }}
          >
            {data.email}
          </Typography>
        )}
        {!data.contact && !data.email && data.id && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            ID: {data.id}
          </Typography>
        )}
      </Box>
    );
  };

  // order type for QR
  const getOrderType = (order: any) => {
    try {
      const inferred = determineOrderType(order);
      return inferred === 'guest' ? 'guest' : 'regular';
    } catch (e) {
      return 'regular';
    }
  };

  // Handle Combined Generation (Invoice + QR)
  const handleGenerateCombined = async (order: Order) => {
    if (!order) return;

    setIsGeneratingCombined(true);
    setSelectedOrder(order);
    setGenerationStep(0);
    setCombinedResult(null);
    setCombinedQrImage(null);
    setCombinedDialogOpen(true);

    try {
      const orderType = getOrderType(order);

      setGenerationStep(1); // Step 1: Generating Invoice

      const result = await generateCombined({
        order_id: order.id,
        user_id: order.user_id,
        order_type: orderType as 'regular' | 'guest',
      }).unwrap();

      setGenerationStep(2); // Step 2: Generating QR

      // Try to get QR image if QR was generated
      if (result.qr) {
        try {
          const imageResult = await triggerGetQrImage({
            order_id: order.id,
            order_type: orderType as 'regular' | 'guest',
          }).unwrap();

          if (imageResult) {
            const imageUrl = URL.createObjectURL(imageResult);
            setCombinedQrImage(imageUrl);
          }
        } catch (imgErr) {
          console.warn('QR image fetch failed:', imgErr);
        }
      }

      setGenerationStep(3); // Step 3: Complete
      setCombinedResult(result);

      setSnackbar({
        open: true,
        message: 'Invoice and QR generated successfully!',
        severity: 'success',
      });

    } catch (err: any) {
      console.error('Failed to generate combined:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error generating invoice and QR',
        severity: 'error',
      });
      setGenerationStep(0);
    } finally {
      setIsGeneratingCombined(false);
    }
  };

  // Handle Download Invoice from Combined
  const handleDownloadCombinedInvoice = () => {
    if (!combinedResult?.invoice?.download_url) {
      setSnackbar({
        open: true,
        message: 'No invoice available',
        severity: 'error',
      });
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = combinedResult.invoice.download_url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = `invoice_${selectedOrder?.id}_${combinedResult.invoice.invoice_no}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: 'Invoice download started',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Failed to download invoice:', err);
      setSnackbar({
        open: true,
        message: 'Failed to download invoice',
        severity: 'error',
      });
    }
  };

  // Handle Download QR from Combined
  const handleDownloadCombinedQR = () => {
    if (!combinedResult?.qr) {
      setSnackbar({
        open: true,
        message: 'No QR code available',
        severity: 'error',
      });
      return;
    }

    try {
      if (combinedQrImage) {
        // Download from blob URL
        const link = document.createElement('a');
        link.href = combinedQrImage;
        link.download = `order_${selectedOrder?.id}_qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (combinedResult.qr.qr_code) {
        // Download from base64
        const cleanBase64 = combinedResult.qr.qr_code.replace(/\s/g, '');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${cleanBase64}`;
        link.download = `order_${selectedOrder?.id}_qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbar({
          open: true,
          message: 'No QR image data available',
          severity: 'warning',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: 'QR code downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Download Failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to download QR code: ${error?.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  // Handle Print Combined (Both Invoice and QR)
  const handlePrintCombined = () => {
    if (!combinedPrintRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: 'Please allow popups to print',
        severity: 'warning',
      });
      return;
    }

    const qrImageSource = combinedQrImage ||
      (combinedResult?.qr?.qr_code ? `data:image/png;base64,${combinedResult.qr.qr_code}` : '');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order #${selectedOrder?.id} - Invoice & QR Code</title>
          <style>
            @media print {
              @page {
                size: A4;
                margin: 20mm;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                color: #333;
              }
              .page-break {
                page-break-before: always;
              }
              .container {
                max-width: 800px;
                margin: 0 auto;
                padding: 20px;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 2px solid #333;
                padding-bottom: 20px;
              }
              .header h1 {
                margin: 0;
                color: #333;
                font-size: 28px;
              }
              .order-info {
                display: flex;
                justify-content: space-between;
                margin-bottom: 30px;
                background: #f5f5f5;
                padding: 20px;
                border-radius: 8px;
              }
              .info-section {
                flex: 1;
              }
              .info-section h3 {
                margin-top: 0;
                color: #666;
                font-size: 16px;
              }
              .info-section p {
                margin: 5px 0;
                font-size: 14px;
              }
              .section-title {
                background: #333;
                color: white;
                padding: 10px;
                margin: 30px 0 15px 0;
                font-size: 18px;
                border-radius: 4px;
              }
              .qr-section {
                text-align: center;
                margin: 30px 0;
                padding: 20px;
                border: 2px solid #333;
                border-radius: 8px;
              }
              .qr-image {
                max-width: 300px;
                margin: 0 auto 20px;
              }
              .qr-image img {
                width: 100%;
                height: auto;
              }
              .pickup-code {
                font-size: 24px;
                font-weight: bold;
                letter-spacing: 3px;
                font-family: monospace;
                background: #f5f5f5;
                padding: 15px;
                border-radius: 4px;
                margin-top: 10px;
              }
              .footer {
                text-align: center;
                margin-top: 50px;
                padding-top: 20px;
                border-top: 1px solid #ccc;
                font-size: 12px;
                color: #666;
              }
              .invoice-note {
                background: #fff8e1;
                padding: 15px;
                border-radius: 4px;
                margin: 20px 0;
                font-size: 14px;
              }
            }
            @media screen {
              .container {
                max-width: 800px;
                margin: 0 auto;
                padding: 20px;
                font-family: Arial, sans-serif;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 2px solid #333;
                padding-bottom: 20px;
              }
              .order-info {
                display: flex;
                justify-content: space-between;
                margin-bottom: 30px;
                background: #f5f5f5;
                padding: 20px;
                borderRadius: 8px;
              }
              .info-section {
                flex: 1;
              }
              .section-title {
                background: #333;
                color: white;
                padding: 10px;
                margin: 30px 0 15px 0;
                font-size: 18px;
                borderRadius: 4px;
              }
              .qr-section {
                text-align: center;
                margin: 30px 0;
                padding: 20px;
                border: 2px solid #333;
                borderRadius: 8px;
              }
              .qr-image {
                max-width: 300px;
                margin: 0 auto 20px;
              }
              .qr-image img {
                width: 100%;
                height: auto;
              }
              .pickup-code {
                font-size: 24px;
                font-weight: bold;
                letter-spacing: 3px;
                fontFamily: 'monospace',
                background: #f5f5f5;
                padding: 15px;
                borderRadius: 4px;
                margin-top: 10px;
              }
              .footer {
                text-align: center;
                margin-top: 50px;
                padding-top: 20px;
                border-top: 1px solid #ccc;
                font-size: 12px;
                color: #666;
              }
              .invoice-note {
                background: #fff8e1;
                padding: 15px;
                borderRadius: 4px;
                margin: 20px 0;
                font-size: 14px;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <!-- Page 1: Invoice Information -->
            <div class="header">
              <h1>INVOICE & QR CODE</h1>
              <p>Order #${selectedOrder?.id}</p>
            </div>
            
            <div class="order-info">
              <div class="info-section">
                <h3>Order Details</h3>
                <p><strong>Order ID:</strong> ${selectedOrder?.id}</p>
                <p><strong>Date:</strong> ${selectedOrder?.order_date ? formatDate(selectedOrder.order_date) : '-'}</p>
                <p><strong>Customer:</strong> ${selectedOrder?.user_name}</p>
                <p><strong>Customer ID:</strong> ${selectedOrder?.user_id}</p>
              </div>
              
              <div class="info-section">
                <h3>Invoice Details</h3>
                <p><strong>Invoice No:</strong> ${combinedResult?.invoice?.invoice_no || 'N/A'}</p>
                <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
                <p><strong>Total Amount:</strong> ${formatCurrency(selectedOrder?.total_order_amount || '0')}</p>
              </div>
            </div>

            <div class="invoice-note">
              <strong>Note:</strong> The full invoice PDF is available for download. This page contains a summary.
            </div>

            <h3 class="section-title">Payment Summary</h3>
            <div style="margin-bottom: 30px;">
              <p><strong>Payment Method:</strong> ${selectedOrder?.cash_on_delivery === 1 ? 'Cash on Delivery' : selectedOrder?.pay_later === 1 ? 'Pay Later' : 'Online Payment'}</p>
              <p><strong>Subtotal:</strong> ${formatCurrency(selectedOrder?.total_amount || '0')}</p>
              <p><strong>Discount:</strong> -${formatCurrency(selectedOrder?.total_discount_amount || '0')}</p>
              <p><strong>Shipping:</strong> ${formatCurrency(selectedOrder?.total_shipping_charge || '0')}</p>
              <p><strong>Tax:</strong> ${formatCurrency(selectedOrder?.total_tax_amount || '0')}</p>
              <p><strong>Total:</strong> ${formatCurrency(selectedOrder?.total_order_amount || '0')}</p>
            </div>

            <!-- Page 2: QR Code -->
            <div class="page-break"></div>
            
            <div class="header">
              <h1></h1>
              <p>Order #${selectedOrder?.id}</p>
            </div>

            <div class="qr-section">
              <div class="qr-image">
                ${qrImageSource ? `<img src="${qrImageSource}" alt="QR Code" />` : '<p>QR Code not available</p>'}
              </div>
              
              <div>
                <h3></h3>
                <div class="pickup-code">
                  ${combinedResult?.qr?.pickup_code || 'N/A'}
                </div>
              </div>
            </div>

            <div class="footer">
              <p>Generated on ${new Date().toLocaleString()}</p>
              <p>Order Management System | ${window.location.hostname}</p>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleGenerateQR = async (order: Order) => {
    try {
      const fullOrder = data?.orders?.find((o) => o.id === order.id) || order;

      const orderType = getOrderType(fullOrder);

      setSnackbar({
        open: true,
        message: `Generating QR Code for ${orderType} order...`,
        severity: 'info',
      });

      const result = await generateQR({
        order_id: order.id,
        order_type: orderType as 'regular' | 'guest',
      }).unwrap();

      if (result.success) {
        setGeneratedQR({
          pickupCode: result.pickup_code,
          qrCode: result.qr_code,
        });

        try {
          const imageResult = await triggerGetQrImage({
            order_id: order.id,
            order_type: orderType as 'regular' | 'guest',
          }).unwrap();

          try {
            const maybeBlob = imageResult as unknown as Blob;
            if (
              maybeBlob &&
              (maybeBlob instanceof Blob ||
                ((maybeBlob as any).size !== undefined && (maybeBlob as any).type !== undefined))
            ) {
              const imageUrl = URL.createObjectURL(maybeBlob as Blob);
              setQrImageUrl(imageUrl);
            } else {
              setQrImageUrl(null);
            }
          } catch (imgInnerErr) {
            console.warn('Unable to create object URL from imageResult', imgInnerErr);
            setQrImageUrl(null);
          }
        } catch (imgErr: any) {
          console.warn('QR image fetch failed (optional):', imgErr);
          setQrImageUrl(null);
        }

        setSelectedOrder(order);
        setQrDialogOpen(true);

        setSnackbar({
          open: true,
          message: `QR Code generated successfully for ${orderType} order!`,
          severity: 'success',
        });
      } else {
        const msg = result.message || 'QR generation failed';
        setSnackbar({
          open: true,
          message: msg,
          severity: msg.toLowerCase().includes('not found') ? 'error' : 'warning',
        });
      }
    } catch (err: any) {
      console.error(' QR Generation Failed:', err);
      const backendMessage = err?.data?.message || err?.message || String(err);
      setSnackbar({
        open: true,
        message: backendMessage.includes('Order not found')
          ? `Order not found. Check whether this is a guest order and use guest QR endpoint.`
          : backendMessage,
        severity: 'error',
      });
    }
  };

  const handleDownloadQR = async () => {
    if (!generatedQR || !selectedOrder) {
      console.error(' Missing data for download:', {
        hasGeneratedQR: !!generatedQR,
        hasSelectedOrder: !!selectedOrder,
      });
      setSnackbar({
        open: true,
        message: 'Missing QR data or selected order. Please generate QR first.',
        severity: 'error',
      });
      return;
    }

    const orderType = getOrderType(selectedOrder);

    try {
      if (qrImageUrl) {
        const link = document.createElement('a');
        link.href = qrImageUrl;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: 'QR Code downloaded successfully!',
          severity: 'success',
        });
        return;
      }

      if (generatedQR.qrCode) {
        const cleanBase64 = generatedQR.qrCode.replace(/\s/g, '');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${cleanBase64}`;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: 'QR Code downloaded successfully!',
          severity: 'success',
        });
        return;
      }

      const apiUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${selectedOrder.id}/qr/image`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API returned ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'QR Code downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error(' Download Failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to download QR code: ${error?.message || 'Unknown error'}. Please try again.`,
        severity: 'error',
      });
    }
  };

  const handlePrintQR = () => {
    if (!qrPrintRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const imageSource = qrImageUrl || `data:image/png;base64,${generatedQR?.qrCode}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order QR Code - #${selectedOrder?.id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              border: 2px solid #000;
              padding: 30px;
              border-radius: 10px;
            }
            .order-info {
              margin-bottom: 20px;
            }
            .order-info h2 {
              margin: 0 0 10px 0;
            }
            .order-info p {
              margin: 5px 0;
              font-size: 14px;
            }
            .qr-image {
              margin: 20px 0;
            }
            .qr-image img {
              max-width: 300px;
              width: 100%;
              background-color: white;
            }
            .pickup-code {
              font-size: 24px;
              font-weight: bold;
              margin-top: 15px;
              letter-spacing: 2px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="order-info">
              <h2>Order #${selectedOrder?.id}</h2>
              <p>Customer: ${selectedOrder?.user_name}</p>
              <p>Date: ${selectedOrder?.order_date
        ? new Date(selectedOrder.order_date).toLocaleDateString()
        : '-'
      }</p>
              <p><strong>Total: AED ${selectedOrder?.total_order_amount || '0'}</strong></p>
            </div>
            <hr />
            <div class="qr-image">
              <img src="${imageSource}" alt="Order QR Code" />
            </div>
            <div class="pickup-code">
              <small>Pickup Code</small><br />
              ${generatedQR?.pickupCode || ''}
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: string | number | undefined) => {
    const n = Number(amount || 0);
    if (Number.isNaN(n)) return `AED ${amount}`;
    return `AED ${n.toFixed(2)}`;
  };

  // Get status chip with proper color
  const getStatusChip = (status: number) => {
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === status);
    return (
      <Chip
        label={statusOption?.label || `Status ${status}`}
        size="small"
        sx={{
          backgroundColor: statusOption?.color || '#9e9e9e',
          color: 'white',
          fontWeight: 600,
          fontSize: '12px',
          minWidth: 'fit-content',
        }}
      />
    );
  };

  const getCommentsForOrder = (orderId: number): OrderComment[] => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return [];
    return order.comments || [];
  };

  const getCommentsForStatus = (orderId: number, status: number): OrderComment[] => {
    const allComments = getCommentsForOrder(orderId);
    return allComments.filter((comment) => comment.status === status);
  };

  const hasCommentsForStatus = (orderId: number, status: number) => {
    return getCommentsForStatus(orderId, status).length > 0;
  };

  const hasAnyComments = (orderId: number) => {
    const comments = getCommentsForOrder(orderId);
    return comments.length > 0;
  };

  // Helper function to construct proper product image URL - MATCHING CATEGORYMANAGEMENT
  const getProductImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath || imagePath.trim() === '') {
      return '';
    }

    // Remove leading slash if present 
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;

    console.log('Original product image path:', imagePath);
    console.log('Clean path:', cleanPath);

    // Check if it's already a full URL
    if (cleanPath.startsWith('http')) {
      console.log('Returning full URL for product:', cleanPath);
      return cleanPath;
    }

    // MATCHING THE CATEGORY MANAGEMENT PATTERN
    // Construct URL using base URL and images directory
    const url = `${BASE_URL}/images/${cleanPath}`;
    console.log('Constructed product image URL:', url);
    return url;
  };

  // Helper function to construct proper image URLs for delivery proofs with fallback
  const getDeliveryProofImageUrl = (imagePath: string): string => {
    if (!imagePath || imagePath.trim() === '') {
      console.warn('Empty image path provided');
      return '';
    }

    console.log('Original delivery proof image path:', imagePath);

    // Remove leading slash if present
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;

    // Check if it's already a full URL
    if (cleanPath.startsWith('http')) {
      console.log('Returning full URL for delivery proof:', cleanPath);
      return cleanPath;
    }

    // Try to determine the correct path based on pattern
    if (cleanPath.includes('uploads/')) {
      // It's already in uploads directory
      const url = `${BASE_URL}/${cleanPath}`;
      console.log('Constructed URL for uploads path:', url);
      return url;
    } else if (cleanPath.includes('images/')) {
      // It's already in images directory
      const url = `${BASE_URL}/${cleanPath}`;
      console.log('Constructed URL for images path:', url);
      return url;
    } else if (cleanPath.includes('backend/')) {
      // It's in backend directory
      const url = `${BASE_URL}/${cleanPath}`;
      console.log('Constructed URL for backend path:', url);
      return url;
    } else {
      // Default to images directory (same as CategoryManagement)
      const url = `${BASE_URL}/images/${cleanPath}`;
      console.log('Constructed default URL (images directory):', url);
      return url;
    }
  };
  // render product items with proper images
  const renderOrderItems = (order: Order | null | undefined) => {
    if (!order) return null;
    const items = order.items || [];

    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No items found for this order.
        </Typography>
      );
    }

    return (
      <Box>
        {items.map((item: any, idx: number) => {
          const productName = item.product_name || item.name || `Product ${item.product_id || ''}`;
          const productImage = item.product_image || item.product_main_image || null;
          const imageUrl = getProductImageUrl(productImage);

          const qty = item.product_quantity || item.quantity || item.qty || 0;
          const unitPrice = item.unit_price || item.price || item.unitPrice || '0';
          const itemTotal = item.itemTotal || item.item_total || (Number(unitPrice) * Number(qty)).toFixed(2);
          const discount = item.discount || item.discountAmt || item.discount_amount || '0';
          const warehouseId = item.warehouse_id ?? item.warehouse?.id ?? item.warehouseId;
          const warehouseName =
            item.warehouse_name ??
            (item.warehouse && (item.warehouse.name || item.warehouse.warehouse_name)) ??
            (item.warehouse?.name) ??
            '-';
          const warehouseExtra = item.warehouse_extra_cost ?? item.warehouse?.extra_cost ?? '0';

          return (
            <Box
              key={item.id || idx}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                p: 2,
                borderRadius: 1,
                border: '1px solid #e9ecef',
                mb: 2,
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  flexShrink: 0,
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                  border: '1px solid #f1f1f1',
                  position: 'relative',
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={productName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                    }}
                  />
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }}>
                    <ImageIcon sx={{ color: '#ccc', fontSize: 32 }} />
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                      No Image
                    </Typography>
                  </Box>
                )}
                {imageUrl && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      fontSize: '10px',
                      px: 0.5,
                      py: 0.25,
                      textAlign: 'center',
                    }}
                  >
                    ID: {item.product_id}
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {productName}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Quantity
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {qty} × {formatCurrency(unitPrice)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Item Total
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatCurrency(itemTotal)}
                    </Typography>
                  </Box>

                  {Number(discount) > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Discount
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="error">
                        -{formatCurrency(discount)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>
                    Warehouse Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Warehouse
                      </Typography>
                      <Typography variant="body2">
                        {warehouseName} (ID: {warehouseId || '-'})
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Extra Cost
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(warehouseExtra)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // Render delivery proof images component
  const renderDeliveryProofImages = (order: Order | null) => {
    if (!order || !(order as any).delivery_proof) {
      return (
        <Typography variant="body2" color="text.secondary">
          No delivery proof available.
        </Typography>
      );
    }

    const deliveryProof = (order as any).delivery_proof;
    const assignmentId = getAssignmentId(order);
    const deliveryType = getDeliveryType(order);

    return (
      <Box sx={{ mt: 2 }}>
        {/* Normal Delivery Proof */}
        {deliveryProof.normal_delivery && deliveryProof.normal_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4caf50' }}>
              Normal Delivery Proof
            </Typography>
            <Grid container spacing={2}>
              {deliveryProof.normal_delivery.map((proof: any, index: number) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Image {index + 1}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        mb: 1,
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {proof.image ? (
                        <img
                          src={getDeliveryProofImageUrl(proof.image)}
                          alt={`Delivery proof ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            console.error('Failed to load delivery proof image via initial path:', img.src);

                            // Try alternative patterns
                            const originalPath = proof.image;
                            const cleanPath = originalPath.startsWith('/') ? originalPath.substring(1) : originalPath;

                            // Try different directory patterns
                            const patterns = [
                              `${BASE_URL}/uploads/${cleanPath}`,
                              `${BASE_URL}/images/${cleanPath}`,
                              `${BASE_URL}/${cleanPath}`,
                              `${BASE_URL}/backend/${cleanPath}`
                            ];

                            let currentIndex = patterns.findIndex(pattern => pattern === img.src);
                            let nextIndex = currentIndex + 1;

                            if (nextIndex < patterns.length) {
                              console.log('Trying next pattern:', patterns[nextIndex]);
                              img.src = patterns[nextIndex];
                            } else {
                              // All patterns failed
                              img.style.display = 'none';
                              const container = img.parentElement;
                              if (container) {
                                container.innerHTML = `
            <div style="text-align: center; padding: 20px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p style="margin-top: 10px; color: #999; font-size: 12px;">
                Image not available<br/>
                <small>Path: ${proof.image}</small>
              </p>
            </div>
          `;
                              }
                            }
                          }}
                          onLoad={() => {
                            console.log('Successfully loaded delivery proof image:', proof.image);
                          }}
                        />
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <ImageIcon sx={{ fontSize: 48, color: '#ccc' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            No image available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(getDeliveryProofImageUrl(proof.image), '_blank')}
                      fullWidth
                    >
                      View Full Image
                    </Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Open Box Delivery Proof */}
        {deliveryProof.openbox_delivery && deliveryProof.openbox_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2196f3' }}>
              Open Box Delivery Proof
            </Typography>
            <Grid container spacing={2}>
              {deliveryProof.openbox_delivery.map((proof: any, index: number) => (
                <React.Fragment key={index}>
                  {/* Photo */}
                  {proof.photo && (
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Photo</Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            mb: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <img
                            src={getDeliveryProofImageUrl(proof.photo)}
                            alt="Open box delivery photo"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              // Show fallback content
                              const container = img.parentElement;
                              if (container) {
                                container.innerHTML = `
                                <div style="text-align: center; padding: 20px;">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                  <p style="margin-top: 10px; color: #999; font-size: 12px;">Photo not available</p>
                                </div>
                              `;
                              }
                            }}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(getDeliveryProofImageUrl(proof.photo), '_blank')}
                          fullWidth
                        >
                          View Photo
                        </Button>
                      </Card>
                    </Grid>
                  )}

                  {/* Signature */}
                  {proof.signature && (
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Signature</Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            mb: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <img
                            src={getDeliveryProofImageUrl(proof.signature)}
                            alt="Customer signature"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              backgroundColor: 'white'
                            }}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              // Show fallback content
                              const container = img.parentElement;
                              if (container) {
                                container.innerHTML = `
                                <div style="text-align: center; padding: 20px;">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                  </svg>
                                  <p style="margin-top: 10px; color: #999; font-size: 12px;">Signature not available</p>
                                </div>
                              `;
                              }
                            }}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(getDeliveryProofImageUrl(proof.signature), '_blank')}
                          fullWidth
                        >
                          View Signature
                        </Button>
                      </Card>
                    </Grid>
                  )}

                  {/* Condition Status */}
                  {proof.condition_status && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Condition Status
                        </Typography>
                        <Chip
                          label={proof.condition_status}
                          size="small"
                          color={proof.condition_status === 'OK' ? 'success' : 'warning'}
                        />
                      </Box>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Box>
        )}

        {/* Partial Delivery Proof */}
        {deliveryProof.partial_delivery && deliveryProof.partial_delivery.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ff9800' }}>
              Partial Delivery Proof
            </Typography>
            <Grid container spacing={2}>
              {deliveryProof.partial_delivery.map((proof: any, index: number) => (
                <Grid item xs={12} key={index}>
                  <Card sx={{ p: 2, border: '1px solid #ff9800' }}>
                    {/* Image */}
                    {proof.image && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Proof Image
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: 300,
                            height: 200,
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            mb: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <img
                            src={getDeliveryProofImageUrl(proof.image)}
                            alt="Partial delivery proof"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              // Show fallback content
                              const container = img.parentElement;
                              if (container) {
                                container.innerHTML = `
                                <div style="text-align: center; padding: 20px;">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                  <p style="margin-top: 10px; color: #999; font-size: 12px;">Image not available</p>
                                </div>
                              `;
                              }
                            }}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(getDeliveryProofImageUrl(proof.image), '_blank')}
                        >
                          View Image
                        </Button>
                      </Box>
                    )}

                    {/* Delivered Items */}
                    {proof.delivered_items && proof.delivered_items.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivered Items</Typography>
                        <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                          <Grid container spacing={1}>
                            {proof.delivered_items.map((item: any, idx: number) => (
                              <Grid item xs={12} key={idx}>
                                <Box sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  borderBottom: idx < proof.delivered_items.length - 1 ? '1px solid #ffe0b2' : 'none'
                                }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {item.name || `Item ${item.id}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ID: {item.id}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2">
                                      {item.deliveredQty || item.deliveredqty || 0} / {item.totalQty || item.totalqty || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Delivered / Total
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Box>
                    )}

                    {/* Reason */}
                    {proof.reason && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Reason</Typography>
                        <Chip
                          label={proof.reason}
                          size="small"
                          color="warning"
                          sx={{ mb: 1 }}
                        />
                      </Box>
                    )}

                    {/* Notes */}
                    {proof.notes && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Notes</Typography>
                        <Paper sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="body2">{proof.notes}</Typography>
                        </Paper>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  // Handle comment dialog close
  const handleCommentDialogClose = () => {
    setCommentDialogOpen(false);
    setCommentText('');
  };

  // Handle Combined Dialog Close
  const handleCombinedDialogClose = () => {
    setCombinedDialogOpen(false);
    setIsGeneratingCombined(false);
    setGenerationStep(0);
    setCombinedResult(null);
    if (combinedQrImage) {
      URL.revokeObjectURL(combinedQrImage);
      setCombinedQrImage(null);
    }
  };

  // Handle Cancel Dialog Close
  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedCancelReason('customer_request');
    setCustomCancelReason('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showDetails ? (
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#202223',
                      fontSize: { xs: '20px', md: '24px' },
                    }}
                  >
                    Orders
                  </Typography>
                </Box>

                <Card
                  sx={{
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    border: '1px solid #e1e3e5',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs
                      value={mainTabValue}
                      onChange={(e, v) => setMainTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        },
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />
                    </Tabs>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderBottom: '1px solid #e1e3e5',
                      display: 'flex',
                      gap: 1,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search orders"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{
                        maxWidth: { xs: '100%', sm: 400 },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 200,
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={getStatusText(value)}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(value),
                                  color: 'white',
                                  fontSize: '11px',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {ORDER_STATUS_OPTIONS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={statusFilter.indexOf(status.value) > -1} />
                            <ListItemText primary={status.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Warehouse dropdown filter */}
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 220,
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <InputLabel>Warehouse</InputLabel>
                      <Select
                        value={warehouseFilter}
                        onChange={handleWarehouseFilterChange}
                        label="Warehouse"
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>All Warehouses</em>
                        </MenuItem>
                        {warehouses.map((w: any) => (
                          <MenuItem key={w.id} value={w.id}>
                            {w.name || `Warehouse ${w.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Payment method filter */}
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 220,
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        multiple
                        value={paymentFilter}
                        onChange={handlePaymentFilterChange}
                        input={<OutlinedInput label="Payment Method" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={
                                  PAYMENT_METHOD_OPTIONS.find((opt) => opt.value === value)?.label ||
                                  value
                                }
                                size="small"
                                sx={{
                                  backgroundColor: '#5c6ac4',
                                  color: 'white',
                                  fontSize: '11px',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {PAYMENT_METHOD_OPTIONS.map((method) => (
                          <MenuItem key={method.value} value={method.value}>
                            <Checkbox checked={paymentFilter.indexOf(method.value) > -1} />
                            <ListItemText primary={method.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {(searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0) && (
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                          minWidth: { xs: '100%', sm: 'auto' },
                          '&:hover': {
                            borderColor: '#202223',
                            bgcolor: 'transparent',
                          },
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>

                  {isLoading && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 300,
                        bgcolor: 'white',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load orders. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && filteredOrders.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 500 }}
                      >
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0
                          ? `No results matching your filters`
                          : 'Orders will appear here when customers place them'}
                      </Typography>
                      {(searchStr || statusFilter.length > 0 || warehouseFilter !== '' || paymentFilter.length > 0) && (
                        <Button
                          variant="outlined"
                          onClick={handleClearFilters}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                          }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                  )}

                  {!isLoading && paginatedOrders.length > 0 && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      <Box sx={{ minWidth: 1450 }}>
                        {/* Header row (desktop) */}
                        <Box
                          sx={{
                            display: { xs: 'none', md: 'grid' },
                            gridTemplateColumns: 'minmax(80px, auto) minmax(150px, auto) minmax(130px, auto) minmax(120px, auto) minmax(100px, auto) minmax(130px, auto) minmax(130px, auto) minmax(150px, auto) minmax(140px, auto)',
                            gap: 3,
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #e1e3e5',
                            bgcolor: '#fafbfb',
                            overflow: 'visible',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Order #
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Customer
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Date
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Warehouse
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Items
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Total Amount
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Status
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Agent
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              textAlign: 'right',
                            }}
                          >
                            Actions
                          </Typography>
                        </Box>

                        {paginatedOrders.map((order: Order, index: number) => (
                          <React.Fragment key={order.id}>
                            <Box
                              sx={{
                                display: { xs: 'none', md: 'grid' },
                                gridTemplateColumns: 'minmax(80px, auto) minmax(150px, auto) minmax(130px, auto) minmax(120px, auto) minmax(100px, auto) minmax(130px, auto) minmax(130px, auto) minmax(150px, auto) minmax(140px, auto)',
                                gap: 3,
                                px: 2,
                                py: 2,
                                alignItems: 'center',
                                borderBottom:
                                  index < paginatedOrders.length - 1
                                    ? '1px solid #e1e3e5'
                                    : 'none',
                                '&:hover': { bgcolor: '#f9fafb' },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                #{order.id}
                              </Typography>

                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: '#202223' }}
                                  noWrap
                                >
                                  {order.user_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  ID: {order.user_id}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="text.secondary">
                                {formatDate(order.order_date)}
                              </Typography>

                              {/* WAREHOUSE COLUMN */}
                              <Box>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {getOrderWarehouseName(order)}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="text.secondary">
                                {order.order_items_count ?? (order.items ? order.items.length : 0)} item
                                {(order.order_items_count ?? (order.items ? order.items.length : 0)) !== 1 ? 's' : ''}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: '#202223' }}
                              >
                                {formatCurrency(order.total_order_amount)}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getStatusChip(order.status)}
                                {hasAnyComments(order.id) && (
                                  <Comment color="info" fontSize="small" />
                                )}
                              </Box>

                              {/* AGENT COLUMN - Fixed with truncation */}
                              <Box>
                                {(() => {
                                  const agent = (order as any).delivery_agent || null;
                                  if (!agent && !order.delivery_agent_name) {
                                    return (
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        Not assigned
                                      </Typography>
                                    );
                                  }

                                  const name = order.delivery_agent_name ||
                                    agent?.first_name ||
                                    agent?.firstName ||
                                    agent?.user_name ||
                                    `${agent?.first_name || ''} ${agent?.last_name || ''}`.trim() ||
                                    `Agent ${agent?.id || ''}`;

                                  const contact = agent?.contact || agent?.mobile || agent?.phone || null;

                                  return (
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#ff6a00',
                                          fontSize: '12px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                        title={name}
                                      >
                                        {name}
                                      </Typography>
                                      {contact && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: 'block',
                                            fontSize: '10px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                          title={contact}
                                        >
                                          {contact}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  justifyContent: 'flex-end',
                                  flexWrap: 'nowrap',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrder(order)}
                                  sx={{
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' },
                                  }}
                                  title="View Details"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleStatusUpdateClick(order)}
                                  sx={{
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' },
                                  }}
                                  title="Update Status"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                {/* Cancel Order Button - Only show for orders that can be cancelled */}
                                {order.status <= 6 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCancelOrderClick(order)}
                                    sx={{
                                      color: '#f44336',
                                      '&:hover': { bgcolor: '#ffebee' },
                                    }}
                                    title="Cancel Order"
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                )}
                                {/* Combined Generate Button */}
                                <IconButton
                                  size="small"
                                  onClick={() => handleGenerateCombined(order)}
                                  disabled={isGeneratingCombined}
                                  sx={{
                                    color: '#9c27b0',
                                    backgroundColor: isGeneratingCombined ? '#f3e5f5' : 'transparent',
                                    '&:hover': {
                                      bgcolor: '#f3e5f5',
                                      color: '#7b1fa2'
                                    },
                                  }}
                                  title="Generate Invoice & QR"
                                >
                                  {isGeneratingCombined ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <FileCopy fontSize="small" />
                                  )}
                                </IconButton>
                                {hasAnyComments(order.id) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewComments(order)}
                                    sx={{
                                      color: '#6d7175',
                                      '&:hover': { bgcolor: '#f1f2f4' },
                                    }}
                                    title="View Comments"
                                  >
                                    <Comment fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            {/* Mobile card view */}
                            <Box
                              sx={{
                                display: { xs: 'block', md: 'none' },
                                m: 2,
                              }}
                            >
                              <Card
                                sx={{
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  borderRadius: 2,
                                  border: '1px solid #e1e3e5',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box sx={{ p: 2 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      mb: 2,
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#202223',
                                          fontSize: '16px',
                                        }}
                                      >
                                        Order #{order.id}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(order.order_date)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      {getStatusChip(order.status)}
                                      {hasAnyComments(order.id) && (
                                        <Comment color="info" fontSize="small" sx={{ ml: 0.5 }} />
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleRow(order.id)}
                                        sx={{ ml: 1 }}
                                      >
                                        {expandedRows.includes(order.id) ? (
                                          <KeyboardArrowUp />
                                        ) : (
                                          <KeyboardArrowDown />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </Box>

                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Customer
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {order.user_name}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        ID
                                      </Typography>
                                      <Typography variant="body2">
                                        {order.user_id}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Warehouse
                                      </Typography>
                                      <Typography variant="body2">
                                        {getOrderWarehouseName(order)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Agent
                                      </Typography>
                                      <Typography variant="body2">
                                        {order.delivery_agent_name || 'Not assigned'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Items
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {order.order_items_count ?? (order.items ? order.items.length : 0)} item
                                        {(order.order_items_count ?? (order.items ? order.items.length : 0)) !== 1 ? 's' : ''}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Total
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#202223',
                                        }}
                                      >
                                        {formatCurrency(order.total_order_amount)}
                                      </Typography>
                                    </Grid>
                                  </Grid>

                                  {/* Expanded content */}
                                  <Collapse in={expandedRows.includes(order.id)}>
                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e1e3e5' }}>
                                      {/* Agent Details in expanded view */}
                                      {order.delivery_agent_name && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                                            Delivery Agent Details
                                          </Typography>
                                          {renderAgentInfo(false, {
                                            name: order.delivery_agent_name,
                                            contact: (order as any).delivery_agent?.contact || null,
                                            id: (order as any).delivery_agent?.id || null
                                          })}
                                        </Box>
                                      )}

                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Visibility />}
                                          onClick={() => handleViewOrder(order)}
                                          sx={{
                                            flex: '1 1 auto',
                                            minWidth: '100px',
                                            textTransform: 'none',
                                            borderColor: '#c9cccf',
                                            color: '#202223',
                                            '&:hover': { borderColor: '#202223' },
                                          }}
                                        >
                                          View
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Edit />}
                                          onClick={() => handleStatusUpdateClick(order)}
                                          sx={{
                                            flex: '1 1 auto',
                                            minWidth: '100px',
                                            textTransform: 'none',
                                            borderColor: '#c9cccf',
                                            color: '#202223',
                                            '&:hover': { borderColor: '#202223' },
                                          }}
                                        >
                                          Update
                                        </Button>
                                        {/* Cancel Order Button */}
                                        {order.status <= 6 && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Cancel />}
                                            onClick={() => handleCancelOrderClick(order)}
                                            sx={{
                                              flex: '1 1 auto',
                                              minWidth: '100px',
                                              textTransform: 'none',
                                              borderColor: '#f44336',
                                              color: '#f44336',
                                              '&:hover': {
                                                borderColor: '#d32f2f',
                                                bgcolor: '#ffebee'
                                              },
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                        {/* Combined Generate Button */}
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={isGeneratingCombined ? <CircularProgress size={16} /> : <FileCopy />}
                                          onClick={() => handleGenerateCombined(order)}
                                          disabled={isGeneratingCombined}
                                          sx={{
                                            flex: '1 1 auto',
                                            minWidth: '100px',
                                            textTransform: 'none',
                                            bgcolor: '#9c27b0',
                                            '&:hover': { bgcolor: '#7b1fa2' },
                                          }}
                                        >
                                          Generate
                                        </Button>
                                        {hasAnyComments(order.id) && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Comment />}
                                            onClick={() => handleViewComments(order)}
                                            sx={{
                                              flex: '1 1 100%',
                                              textTransform: 'none',
                                              borderColor: '#c9cccf',
                                              color: '#202223',
                                              '&:hover': { borderColor: '#202223' },
                                            }}
                                          >
                                            Comments
                                          </Button>
                                        )}
                                      </Box>
                                    </Box>
                                  </Collapse>

                                  {/* Quick actions when not expanded */}
                                  {!expandedRows.includes(order.id) && (
                                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleViewOrder(order)}
                                        sx={{
                                          flex: 1,
                                          textTransform: 'none',
                                          borderColor: '#c9cccf',
                                          color: '#202223',
                                          '&:hover': { borderColor: '#202223' },
                                        }}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleStatusUpdateClick(order)}
                                        sx={{
                                          flex: 1,
                                          textTransform: 'none',
                                          borderColor: '#c9cccf',
                                          color: '#202223',
                                          '&:hover': { borderColor: '#202223' },
                                        }}
                                      >
                                        Update
                                      </Button>
                                      {order.status <= 6 && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => handleCancelOrderClick(order)}
                                          sx={{
                                            flex: 1,
                                            textTransform: 'none',
                                            borderColor: '#f44336',
                                            color: '#f44336',
                                            '&:hover': {
                                              borderColor: '#d32f2f',
                                              bgcolor: '#ffebee'
                                            },
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Card>
                            </Box>
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, newPage) => setPage(newPage)}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // ------------- ORDER DETAILS (VIEW SCREEN) -------------
              <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' },
                    }}
                  >
                    Orders
                  </Button>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: '#202223',
                        fontSize: { xs: '20px', md: '24px' },
                      }}
                    >
                      Order #{selectedOrder?.id}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {getStatusChip(selectedOrder?.status || 0)}
                    </Box>
                  </Box>
                </Box>

                {/* FULL-WIDTH DESKTOP GRID INSTEAD OF SINGLE COLUMN STACK */}
                <Grid container spacing={2}>
                  {/* Customer */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#202223',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Person /> Customer
                        </Typography>
                        
                        {isLoadingDetails ? (
                          <Box>
                            <Skeleton variant="text" width="40%" height={24} />
                            <Skeleton variant="text" width="60%" height={20} />
                          </Box>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Name:</strong> {customerData?.name || selectedOrder?.user_name}
                            </Typography>
                            <Typography variant="body2">
                              <strong>User ID:</strong> {customerData?.userId || selectedOrder?.user_id}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Delivery Agent */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Delivery Agent
                        </Typography>
                        {renderAgentInfo(isLoadingDetails, deliveryAgentData)}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Warehouse Information */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Warehouse Information
                        </Typography>
                        
                        {isLoadingDetails ? (
                          <Box>
                            <Skeleton variant="text" width="70%" height={24} />
                            <Skeleton variant="text" width="50%" height={20} />
                          </Box>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Warehouse:</strong> {warehouseData?.name || getOrderWarehouseName(selectedOrder || {})}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Order Date:</strong> {formatDate(warehouseData?.orderDate || selectedOrder?.order_date || '')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Delivery Information Card */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Delivery Information
                        </Typography>

                        {isLoadingDetails ? (
                          <Box>
                            <Skeleton variant="text" width="50%" height={24} />
                            <Skeleton variant="text" width="80%" height={20} />
                            <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2, borderRadius: 1 }} />
                          </Box>
                        ) : (
                          <>
                            {/* Delivery Type */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>
                                Delivery Type
                              </Typography>
                              <Chip
                                label={deliveryInfoData?.deliveryType || getDeliveryType(selectedOrder)}
                                size="small"
                                sx={{
                                  backgroundColor: getDeliveryTypeColor(deliveryInfoData?.deliveryType || getDeliveryType(selectedOrder)),
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            </Box>

                            {/* Assignment ID */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>
                                Assignment ID
                              </Typography>
                              <Typography variant="body2" sx={{
                                fontFamily: 'monospace',
                                backgroundColor: '#f5f5f5',
                                p: 1,
                                borderRadius: 1,
                                wordBreak: 'break-all'
                              }}>
                                {deliveryInfoData?.assignmentId || getAssignmentId(selectedOrder)}
                              </Typography>
                            </Box>

                            {/* Delivery Proof Images */}
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#666' }}>
                                Delivery Proof
                              </Typography>
                              {renderDeliveryProofImages(selectedOrder)}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Payment Summary */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#202223',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <AttachMoney /> Payment Summary
                        </Typography>

                        {/* Payment Method */}
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>
                            Payment Method
                          </Typography>
                          {selectedOrder?.cash_on_delivery === 1 ? (
                            <Chip
                              label="Cash on Delivery"
                              size="small"
                              sx={{
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          ) : selectedOrder?.pay_later === 1 ? (
                            <Chip
                              label="Pay Later"
                              size="small"
                              sx={{
                                backgroundColor: '#2196f3',
                                color: 'white',
                                fontWeight: 600,
                                mb: 1
                              }}
                            />
                          ) : (
                            <Chip
                              label="Online Payment"
                              size="small"
                              sx={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Subtotal
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_amount || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Discount
                          </Typography>
                          <Typography variant="body2">
                            -{formatCurrency(selectedOrder?.total_discount_amount || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Shipping
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_shipping_charge || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Tax
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_tax_amount || '0')}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            pt: 2,
                            borderTop: '2px solid #e1e3e5',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Total
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: '16px' }}
                          >
                            {formatCurrency(selectedOrder?.total_order_amount || '0')}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Actions
                        </Typography>
                        <Stack spacing={1}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              selectedOrder && handleStatusUpdateClick(selectedOrder)
                            }
                            startIcon={<Edit />}
                            sx={{
                              bgcolor: '#008060',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#006e52' },
                            }}
                          >
                            Update Status
                          </Button>
                          {/* Cancel Order Button - Only show for orders that can be cancelled */}
                          {selectedOrder && selectedOrder.status <= 6 && (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() =>
                                selectedOrder && handleCancelOrderClick(selectedOrder)
                              }
                              startIcon={<Cancel />}
                              sx={{
                                bgcolor: '#f44336',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#d32f2f' },
                              }}
                            >
                              Cancel Order
                            </Button>
                          )}
                          {/* Combined Generate Button */}
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              selectedOrder && handleGenerateCombined(selectedOrder)
                            }
                            startIcon={<FileCopy />}
                            disabled={isGeneratingCombined}
                            sx={{
                              bgcolor: '#9c27b0',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#7b1fa2' },
                            }}
                          >
                            {isGeneratingCombined ? 'Generating...' : 'Generate Invoice & QR'}
                          </Button>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              selectedOrder && handleGenerateQR(selectedOrder)
                            }
                            startIcon={<QrCode2 />}
                            disabled={isGeneratingQR}
                            sx={{
                              bgcolor: '#5c6ac4',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#4c5ab8' },
                            }}
                          >
                            {isGeneratingQR ? 'Generating...' : 'Generate QR Code'}
                          </Button>
                          {hasAnyComments(selectedOrder?.id || 0) && (
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() =>
                                selectedOrder && handleViewComments(selectedOrder)
                              }
                              startIcon={<Comment />}
                              sx={{
                                textTransform: 'none',
                                borderColor: '#c9cccf',
                                color: '#202223',
                                borderRadius: '8px',
                                fontWeight: 500,
                                '&:hover': {
                                  borderColor: '#202223',
                                  bgcolor: 'transparent',
                                },
                              }}
                            >
                              View All Comments
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>

                  {/* PRODUCTS / ITEMS - full width card */}
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Products ({selectedOrder?.order_items_count ?? (selectedOrder?.items ? selectedOrder.items.length : 0)})
                        </Typography>

                        {renderOrderItems(selectedOrder)}
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

          </Box>
        </Box>
      </Box>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#18badb',
            color: 'white',
          }}
        >
          <Typography variant="h6">Cancel Order #{selectedOrder?.id}</Typography>
          <IconButton onClick={handleCancelDialogClose} size="small" sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              You are about to cancel this order. This action cannot be undone.
            </Typography>
            
            {selectedOrder && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#bbe9ef', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Order Summary:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Customer:</strong> {selectedOrder.user_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Amount:</strong> {formatCurrency(selectedOrder.total_order_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Paymen t:</strong> {selectedOrder.cash_on_delivery === 1 ? 'Cash on Delivery' : selectedOrder.pay_later === 1 ? 'Pay Later' : 'Online Payment'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Items:</strong> {selectedOrder.order_items_count ?? (selectedOrder.items ? selectedOrder.items.length : 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Refund Information */}
            {selectedOrder && (selectedOrder.cash_on_delivery === 1 || selectedOrder.pay_later === 1) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalanceWallet sx={{ mr: 1, color: '#2e7d32' }} />
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                    Automatic Refund
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {selectedOrder.cash_on_delivery === 1 
                    ? ` ${formatCurrency(selectedOrder.total_order_amount)} will be refunded to customer's wallet.`
                    : ` ${formatCurrency(selectedOrder.total_order_amount)} will be refunded to customer's Pay Later balance.`
                  }
                </Typography>
              </Box>
            )}

            {/* Cancellation Type */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cancellation Type
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={cancelType}
                  onChange={(e) => setCancelType(e.target.value as 'admin' | 'user')}
                >
                  <FormControlLabel
                    value="admin"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Admin Cancellation</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Order status will be: "Order cancelled by admin"
                        </Typography>
                      </Box>
                    }
                  />
                  {/* <FormControlLabel
                    value="user"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">User Cancellation</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Order status will be: "Cancelled by user"
                        </Typography>
                      </Box>
                    }
                  /> */}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Cancellation Reason */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Cancellation Reason
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Reason</InputLabel>
                <Select
                  value={selectedCancelReason}
                  onChange={(e) => setSelectedCancelReason(e.target.value)}
                  label="Select Reason"
                >
                  {CANCEL_REASON_OPTIONS.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCancelReason === 'other' && (
                <TextField
                  fullWidth
                  label="Custom Reason"
                  multiline
                  rows={3}
                  value={customCancelReason}
                  onChange={(e) => setCustomCancelReason(e.target.value)}
                  placeholder="Please specify the cancellation reason..."
                  sx={{ mb: 2 }}
                />
              )}

              <Typography variant="caption" color="text.secondary">
                This reason will be recorded in the order history and may be shared with the customer.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCancelDialogClose} disabled={isCancelling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelOrder}
            disabled={isCancelling || (selectedCancelReason === 'other' && !customCancelReason.trim())}
            startIcon={isCancelling ? <CircularProgress size={20} /> : <Cancel />}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Combined Generation Dialog */}
      <Dialog
        open={combinedDialogOpen}
        onClose={handleCombinedDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: '600px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#9c27b0',
            color: 'white',
          }}
        >
          <Typography variant="h6">Generate Invoice & QR Code</Typography>
          <IconButton onClick={handleCombinedDialogClose} size="small" sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {isGeneratingCombined ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <Stepper activeStep={generationStep} orientation="vertical" sx={{ width: '100%' }}>
                {generationSteps.map((step, index) => (
                  <Step key={step.label} completed={generationStep > index}>
                    <StepLabel
                      StepIconProps={{
                        icon: step.icon,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {step.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {index === 0 && generationStep === 1 && (
                        <Box sx={{ pl: 4, py: 2 }}>
                          <CircularProgress size={20} sx={{ mr: 2 }} />
                          <Typography variant="body2">Generating invoice PDF...</Typography>
                        </Box>
                      )}
                      {index === 1 && generationStep === 2 && (
                        <Box sx={{ pl: 4, py: 2 }}>
                          <CircularProgress size={20} sx={{ mr: 2 }} />
                          <Typography variant="body2">Creating QR code...</Typography>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {generationStep < 3 && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ color: '#9c27b0' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Please wait while we generate everything...
                  </Typography>
                </Box>
              )}
            </Box>
          ) : combinedResult ? (
            <Box ref={combinedPrintRef}>
              <Grid container spacing={3}>
                {/* Page 1: Invoice Section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mb: 3, border: '2px solid #e0e0e0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                        <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                        INVOICE
                      </Typography>
                      <Chip
                        label={combinedResult.invoice ? "Generated" : "Failed"}
                        color={combinedResult.invoice ? "success" : "error"}
                        icon={combinedResult.invoice ? <CheckCircle /> : <ErrorIcon />}
                      />
                    </Box>

                    {combinedResult.invoice ? (
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Invoice Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {combinedResult.invoice.invoice_no}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Order Details
                              </Typography>
                              <Typography variant="body2">
                                Order #{selectedOrder?.id} | {selectedOrder?.user_name}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, p: 3, bgcolor: '#fff8e1', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Invoice PDF is ready for download.</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            The PDF contains complete invoice details including items, taxes, and totals.
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="error" gutterBottom>
                          Invoice Generation Failed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {combinedResult.message || 'Unable to generate invoice'}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      {combinedResult.invoice && (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={handleDownloadCombinedInvoice}
                            sx={{ textTransform: 'none' }}
                          >
                            Download Invoice
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Print />}
                            onClick={() => {
                              window.open(combinedResult.invoice!.download_url, '_blank');
                              setSnackbar({
                                open: true,
                                message: 'Invoice opened in new tab. Use Ctrl+P to print.',
                                severity: 'info',
                              });
                            }}
                            sx={{
                              textTransform: 'none',
                              bgcolor: '#9c27b0',
                              '&:hover': { bgcolor: '#7b1fa2' },
                            }}
                          >
                            Open Invoice
                          </Button>
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Page 2: QR Code Section */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, border: '2px solid #e0e0e0', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ color: '#008060', fontWeight: 'bold' }}>
                        <QrCode2 sx={{ mr: 1, verticalAlign: 'middle' }} />
                        QR CODE FOR PICKUP
                      </Typography>
                      <Chip
                        label={combinedResult.qr ? "Generated" : "Failed"}
                        color={combinedResult.qr ? "success" : "error"}
                        icon={combinedResult.qr ? <CheckCircle /> : <ErrorIcon />}
                      />
                    </Box>

                    {combinedResult.qr ? (
                      <Box>
                        <Grid container spacing={4}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Box
                                sx={{
                                  width: '100%',
                                  maxWidth: 300,
                                  mx: 'auto',
                                  p: 3,
                                  border: '2px solid #e0e0e0',
                                  borderRadius: 2,
                                  bgcolor: 'white',
                                  mb: 3,
                                }}
                              >
                                {combinedQrImage ? (
                                  <img
                                    src={combinedQrImage}
                                    alt="QR Code"
                                    style={{
                                      width: '100%',
                                      height: 'auto'
                                    }}
                                  />
                                ) : (
                                  <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <CircularProgress />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                      Loading QR Code...
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                PICKUP INFORMATION
                              </Typography>

                              <Box sx={{ mb: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  PICKUP CODE
                                </Typography>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 'bold',
                                    fontFamily: 'monospace',
                                    letterSpacing: 3,
                                    color: '#333',
                                    bgcolor: 'white',
                                    p: 2,
                                    borderRadius: 1,
                                    mt: 1,
                                  }}
                                >
                                  {combinedResult.qr.pickup_code}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Instructions:</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  1. Scan QR code or enter pickup code
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  2. Verify order details
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  3. Hand over items to customer
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" color="error" gutterBottom>
                          QR Code Generation Failed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {combinedResult.message || 'Unable to generate QR code'}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      {combinedResult.qr && (
                        <>
                          <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={handleDownloadCombinedQR}
                            sx={{ textTransform: 'none' }}
                          >
                            Download QR
                          </Button>
                          {/* <Button
                            variant="contained"
                            startIcon={<Print />}
                            onClick={handlePrintCombined}
                            sx={{
                              textTransform: 'none',
                              bgcolor: '#008060',
                              '&:hover': { bgcolor: '#006e52' },
                            }}
                          >
                            Print Both
                          </Button> */}
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Summary Section */}
              <Paper sx={{ p: 3, mt: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  GENERATION SUMMARY
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Order ID
                      </Typography>
                      <Typography variant="h6">#{selectedOrder?.id}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Customer
                      </Typography>
                      <Typography variant="body1">{selectedOrder?.user_name}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Invoice
                      </Typography>
                      <Chip
                        label={combinedResult.invoice ? "✓ Ready" : "✗ Failed"}
                        color={combinedResult.invoice ? "success" : "error"}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        QR Code
                      </Typography>
                      <Chip
                        label={combinedResult.qr ? "✓ Ready" : "✗ Failed"}
                        color={combinedResult.qr ? "success" : "error"}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Ready to generate invoice and QR code for Order #{selectedOrder?.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click the Generate button to start
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button onClick={handleCombinedDialogClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>

          {combinedResult && (
            <>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrintCombined}
                disabled={!combinedResult.invoice || !combinedResult.qr}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#9c27b0',
                  '&:hover': { bgcolor: '#7b1fa2' },
                }}
              >
                Print Both Pages
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => {
          setQrDialogOpen(false);
          if (qrImageUrl) {
            try {
              URL.revokeObjectURL(qrImageUrl);
            } catch { }
            setQrImageUrl(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Order QR Code</Typography>
          <IconButton
            onClick={() => {
              setQrDialogOpen(false);
              if (qrImageUrl) {
                try {
                  URL.revokeObjectURL(qrImageUrl);
                } catch { }
                setQrImageUrl(null);
              }
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div ref={qrPrintRef}>
            <Box className="qr-container" sx={{ textAlign: 'center', p: 3 }}>
              <Box className="order-info" sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Order #{selectedOrder?.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {selectedOrder?.user_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date:{' '}
                  {selectedOrder?.order_date
                    ? formatDate(selectedOrder.order_date)
                    : '-'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mt: 1 }}
                >
                  Total:{' '}
                  {selectedOrder?.total_order_amount
                    ? formatCurrency(selectedOrder.total_order_amount)
                    : '-'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {generatedQR && (
                <>
                  <Box
                    className="qr-image"
                    sx={{ display: 'flex', justifyContent: 'center', my: 3 }}
                  >
                    {isLoadingQrImage ? (
                      <CircularProgress />
                    ) : (
                      <img
                        src={
                          qrImageUrl ||
                          `data:image/png;base64,${generatedQR.qrCode}`
                        }
                        alt="Order QR Code"
                        style={{
                          maxWidth: '300px',
                          width: '100%',
                          border: '2px solid #e1e3e5',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                        }}
                        onError={(e) => {
                          console.error('Image failed to load');
                          const img = e.target as HTMLImageElement;
                          if (!img.src.startsWith('data:') && generatedQR?.qrCode) {
                            img.src = `data:image/png;base64,${generatedQR.qrCode}`;
                          }
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    className="pickup-code"
                    sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 3,
                        fontFamily: 'monospace',
                      }}
                    >
                      {generatedQR.pickupCode}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => {
              setQrDialogOpen(false);
              if (qrImageUrl) {
                try {
                  URL.revokeObjectURL(qrImageUrl);
                } catch { }
                setQrImageUrl(null);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadQR}
            disabled={!generatedQR}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintQR}
            disabled={!generatedQR}
            sx={{
              textTransform: 'none',
              bgcolor: '#008060',
              '&:hover': { bgcolor: '#006e52' },
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => {
                const newStatus = Number(e.target.value);
                setSelectedStatus(newStatus);
              }}
              label="Order Status"
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block' }}
          >
            {requiresComment(selectedStatus)
              ? '⚠️ Comment is required for this status update'
              : '💡 Comment is optional for this status update'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              if (requiresComment(selectedStatus)) {
                setStatusDialogOpen(false);
                setCommentDialogOpen(true);
              } else {
                handleStatusUpdate();
                setStatusDialogOpen(false);
              }
            }}
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {requiresComment(selectedStatus) ? 'Add Comment' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={handleCommentDialogClose}
        maxWidth="sm"
        fullWidth
        
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          Update Order Status{' '}
          {requiresComment(selectedStatus) ? '(Comment Required)' : '(Comment Optional)'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => {
                const newStatus = Number(e.target.value);
                setSelectedStatus(newStatus);
                // Auto-fill comment for required statuses when empty
                if (requiresComment(newStatus) && !commentText.trim()) {
                  const newStatusOption = ORDER_STATUS_OPTIONS.find(opt => opt.value === newStatus);
                  setCommentText(`Status updated to: ${newStatusOption?.label || getStatusText(newStatus)}`);
                }
              }}
              label="Order Status"
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              requiresComment(selectedStatus)
                ? 'Add a comment (required)'
                : 'Add a comment (optional)'
            }
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={`Status updated to: ${ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus)?.label || getStatusText(selectedStatus)}`}
            required={requiresComment(selectedStatus)}
            error={requiresComment(selectedStatus) && !commentText.trim()}
            helperText={
              requiresComment(selectedStatus) && !commentText.trim()
                ? 'Comment is required for this status'
                : ''
            }
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {requiresComment(selectedStatus) ? (
              <>
                Comments are required for status changes to: Not Delivered, Cancelled, Return, and Delivery statuses.
                <br />
                <strong>Suggested comment:</strong> Status updated to: {ORDER_STATUS_OPTIONS.find(opt => opt.value === selectedStatus)?.label || getStatusText(selectedStatus)}
              </>
            ) : (
              'Comments are optional for this status change.'
            )}
          </Typography>

          {selectedOrder &&
            hasCommentsForStatus(selectedOrder.id, selectedStatus) && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Existing comments for this status:
                </Typography>
                {getCommentsForStatus(selectedOrder.id, selectedStatus)
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.created_at || '').getTime() -
                      new Date(a.created_at || '').getTime()
                  )
                  .map((comment, index) => (
                    <CommentItem key={comment.id || index} comment={comment} />
                  ))}
              </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCommentDialogClose}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommentSubmit}
            disabled={isUpdating || (requiresComment(selectedStatus) && !commentText.trim())}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Comments Dialog */}
      <Dialog
        open={viewCommentsOpen}
        onClose={() => setViewCommentsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>All Comments for Order #{selectedOrder?.id}</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <CommentsList
              orderId={selectedOrder.id}
              maxHeight={500}
              showDeleteAllButton={true}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setViewCommentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Comments Dialog */}
      <Dialog
        open={deleteAllCommentsDialogOpen}
        onClose={() => setDeleteAllCommentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Confirm Delete All Comments</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete all comments for Order #{selectedOrder?.id}?
          </Typography>

          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {deletableComments.length === (selectedOrder?.comments?.length || 0)
              ? `This will delete ${deletableComments.length} comment${deletableComments.length !== 1 ? 's' : ''
              } permanently. This action cannot be undone.`
              : `This will delete ${deletableComments.length} of ${selectedOrder?.comments?.length || 0
              } comment${(selectedOrder?.comments?.length || 0) !== 1 ? 's' : ''
              } permanently (only comments with statuses 5–17 are deletable). This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeleteAllCommentsDialogOpen(false)}
            disabled={isDeletingComment}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteAllComments}
            disabled={isDeletingComment || deletableComments.length === 0}
            startIcon={isDeletingComment ? <CircularProgress size={20} /> : null}
          >
            {isDeletingComment ? 'Deleting...' : 'Delete All Comments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Order Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Confirm Delete Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete Order #{selectedOrder?.id}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone. All order data, including items and comments, will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOrder}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement;